/**
 * Auth context: provee el usuario actual (o null si no autenticado),
 * acciones login/logout, y estado de carga inicial.
 *
 * Estrategia:
 *  - En mount, llama GET /auth/me. Si 200 → user autenticado (cookie
 *    JWT vigente). Si 401 → user = null.
 *  - `login(username, password)` hace POST /auth/login → backend setea
 *    cookie HttpOnly → repetimos GET /auth/me para hidratar el user.
 *  - `logout()` hace POST /auth/logout (backend borra la cookie) y
 *    limpia el user en memoria.
 *  - Configura `setUnauthorizedHandler` en mount → cualquier 401 en
 *    cualquier request global redirige al /login (vía useNavigate al
 *    componente que consume).
 */

import { createContext, useEffect, useState, type ReactNode } from "react";
import { api, setUnauthorizedHandler } from "../api/client";
import { queryClient } from "../lib/queryClient";
import { LS_KEY as WORKSPACE_LS_KEY } from "../workspace/WorkspaceContext";
import type { MeResponse } from "../api/types";

interface AuthState {
  user: MeResponse | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  // Bandera interna: AuthContext la setea en true cuando algún 401
  // global dispara el handler. ProtectedRoute la lee y redirige.
  forcedLogout: boolean;
  ackForcedLogout: () => void;
}

export const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [forcedLogout, setForcedLogout] = useState(false);

  // Configura el handler global: cualquier 401 en cualquier request
  // arrastra al user a /login sin necesidad de manejar el error en
  // cada query individual.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setForcedLogout(true);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Hidrata user al primer mount (refresca después de F5).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<MeResponse>("/auth/me");
        if (!cancelled) setUser(res.data);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Rotación de sesión: mientras haya usuario, re-emite el token cada 20 min.
  // El token expira en 60 min; si el usuario está inactivo, no se refresca y la
  // sesión caduca (sin sesiones eternas). Un 401 lo arrastra el handler global.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(
      () => {
        api.post("/auth/refresh").catch(() => {
          /* si falla, el siguiente request 401 dispara el logout forzado */
        });
      },
      20 * 60 * 1000,
    );
    return () => clearInterval(id);
  }, [user]);

  const login = async (username: string, password: string): Promise<void> => {
    // Limpia el cache de queries del usuario anterior para que la vista no quede stale.
    queryClient.clear();
    await api.post("/auth/login", { username, password });
    const res = await api.get<MeResponse>("/auth/me");
    // Workspace por defecto gana en cada login: si el usuario tiene uno
    // configurado, lo fijamos como activo antes de que el WorkspaceContext
    // lo lea desde localStorage (evita quedar sin workspace tras el login).
    // Esto solo ocurre en login explícito; el F5 pasa por el hidratado de
    // mount y respeta el workspace elegido durante la sesión. Si el usuario
    // no tiene default, no tocamos localStorage: se conserva su última
    // selección (el WorkspaceContext auto-selecciona el primero si no hay).
    if (typeof window !== "undefined" && res.data.default_workspace_id) {
      window.localStorage.setItem(WORKSPACE_LS_KEY, res.data.default_workspace_id);
    }
    setUser(res.data);
    setForcedLogout(false);
  };

  const logout = async (): Promise<void> => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Si el logout falla en server (raro), igual limpiamos cliente.
    }
    setUser(null);
    queryClient.clear();  // descarta datos cacheados del usuario que sale
  };

  const ackForcedLogout = (): void => setForcedLogout(false);

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, forcedLogout, ackForcedLogout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
